<template>
  <b-modal
    id="product-batch-stock-history-modal"
    :visible="visible"
    :title="modalTitle"
    size="xl"
    hide-footer
    centered
    @change="onVisibilityChange"
    @hidden="onHidden"
  >
    <div class="batch-stock-history">
      <b-row class="mb-3 align-items-center">
        <b-col md="5">
          <div class="date-style">
            <b-input-group>
              <date-picker
                placeholder="Từ"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                v-model="queryParams.fromDate"
              ></date-picker>
            </b-input-group>
            <span class="ml-1 mr-1"></span>
            <b-input-group>
              <date-picker
                placeholder="Đến"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                v-model="queryParams.toDate"
              ></date-picker>
            </b-input-group>
          </div>
        </b-col>
        <b-col md="auto">
          <b-button
            style="width: 70px"
            size="sm"
            class="font-weight-bolder"
            variant="primary"
            @click="onFilter"
            :disabled="isBusy"
          >
            <i class="fas fa-search text-white fa-lg"></i>Lọc
          </b-button>
        </b-col>
        <b-col md="auto">
          <b-overlay
            :show="isDownloading"
            rounded
            opacity="0.6"
            class="d-inline-block"
          >
            <template #overlay>
              <b-spinner variant="light" small type="grow"></b-spinner>
            </template>
            <b-button
              size="sm"
              class="font-weight-bolder"
              variant="success"
              @click="exportExcel"
              :disabled="isBusy || isDownloading || !totalItems"
            >
              <v-icon small class="text-white">mdi-download</v-icon>
              Xuất Excel
            </b-button>
          </b-overlay>
        </b-col>
      </b-row>

      <div class="history-table-scroll">
        <b-table
          :items="data"
          :fields="fields"
          :busy="isBusy"
          bordered
          hover
          small
          class="table-bordered table-hover"
        >
          <template #table-busy>
            <div class="text-center text-dark my-2">
              <b-spinner class="align-middle mr-2"></b-spinner>
              <strong>Đang tải...</strong>
            </div>
          </template>
          <template #cell(createdAt)="row">
            {{ formatDateTime(row.item.createdAt) }}
          </template>
          <template #cell(transactionTypeLabel)="row">
            <b-badge
              :variant="row.item.transactionType === 1 ? 'success' : 'danger'"
            >
              {{ row.item.transactionTypeLabel || '—' }}
            </b-badge>
          </template>
          <template #cell(description)="row">
            {{ row.item.description || '—' }}
          </template>
          <template #cell(quantity)="row">
            <span style="font-weight: 600">{{ row.item.quantity }}</span>
          </template>
          <template #cell(stockSlipCode)="row">
            <a
              v-if="row.item.stockSlipId && row.item.stockSlipCode"
              :href="stockSlipHref(row.item)"
              target="_blank"
              rel="noopener noreferrer"
              :title="`Nhấn để mở chi tiết phiếu ${row.item.stockSlipCode}`"
              class="stock-slip-link"
              @click.stop
            >{{ row.item.stockSlipCode }}</a>
            <span v-else>{{ row.item.stockSlipCode || '—' }}</span>
          </template>
          <template #empty>
            <div class="text-center text-dark my-3">
              Không có lịch sử cho lô này
            </div>
          </template>
        </b-table>
      </div>

      <b-row class="align-items-center">
        <b-col>
          <p class="mb-0 text-dark font-weight-bold">
            Hiển thị {{ startIndex }} - {{ endIndex }} trong số
            {{ totalItems }} bản ghi
          </p>
        </b-col>
        <b-col>
          <b-pagination
            pills
            v-show="totalPages >= 2"
            v-model="queryParams.page"
            :total-rows="totalItems"
            :per-page="queryParams.limit"
            @change="handlePageChange"
            align="right"
            class="mb-0"
            style="border-radius: 5px"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { SUCCESS_RESPONSE_STATUS } from '@/utils/constants';
import { DP_CONFIG, formatDBDate, getSubtractDate, formatDate } from '@/utils/date';
import { paginationMixin } from '@/view/mixins';
import fileDownload from '@/utils/file-download';

export default {
  name: 'ListProductBatchStockHistory',
  mixins: [paginationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    productBatchStockId: {
      type: [Number, String],
      default: null,
    },
    batchCode: {
      type: String,
      default: '',
    },
    productName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      data: [],
      isBusy: false,
      isDownloading: false,
      dpConfigs: DP_CONFIG,
      fields: [
        { key: 'createdAt', label: 'Thời gian' },
        {
          key: 'transactionTypeLabel',
          label: 'Loại giao dịch',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'description',
          label: 'Diễn giải',
          tdClass: 'text-dark',
        },
        {
          key: 'quantity',
          label: 'SL thay đổi',
          tdClass: 'text-right',
        },
        {
          key: 'beforeQuantity',
          label: 'Tồn trước',
          tdClass: 'text-right',
        },
        {
          key: 'afterQuantity',
          label: 'Tồn sau',
          tdClass: 'text-right',
        },
        { key: 'stockSlipCode', label: 'Mã phiếu' },
        { key: 'createdByName', label: 'Người thao tác' },
      ],
      queryParams: {
        page: 1,
        limit: 10,
        fromDate: getSubtractDate(30),
        toDate: null,
      },
    };
  },
  computed: {
    modalTitle() {
      let title = 'Lịch sử sử dụng lô';
      if (this.batchCode) title += `: ${this.batchCode}`;
      if (this.productName) title += ` - ${this.productName}`;
      return title;
    },
    startIndex() {
      if (!this.totalItems) return 0;
      return (this.queryParams.page - 1) * this.queryParams.limit + 1;
    },
    endIndex() {
      const end = this.queryParams.page * this.queryParams.limit;
      return end > this.totalItems ? this.totalItems : end;
    },
  },
  watch: {
    visible(val) {
      if (val && this.productBatchStockId) {
        this.resetAndFetch();
      }
    },
  },
  methods: {
    resetAndFetch() {
      this.queryParams.page = 1;
      this.queryParams.fromDate = getSubtractDate(30);
      this.queryParams.toDate = null;
      this.fetchHistory();
    },
    onVisibilityChange(val) {
      this.$emit('update:visible', val);
    },
    onHidden() {
      this.data = [];
      this.$emit('update:visible', false);
    },
    fetchHistory() {
      if (this.isBusy || !this.productBatchStockId) return;
      this.isBusy = true;
      const params = {
        page: this.queryParams.page,
        limit: this.queryParams.limit,
        fromDate: formatDBDate(this.queryParams.fromDate),
        toDate: formatDBDate(this.queryParams.toDate),
      };
      ApiService.query(
        `product-batch-stock/${this.productBatchStockId}/history`,
        { params },
      )
        .then(this.handleMappingResult)
        .catch(this.handleError)
        .finally(() => (this.isBusy = false));
    },
    handleMappingResult({ data }) {
      if (data.status === SUCCESS_RESPONSE_STATUS) {
        this.data = data.data.result;
        this.mapPagination(data);
      }
    },
    handleError(error) {
      this.isBusy = false;
      const message =
        error.response && error.response.data
          ? error.response.data.message
          : 'Lỗi tải lịch sử lô';
      makeToastFaile(message || 'Lỗi');
    },
    formatDateTime(value) {
      return value ? formatDate(value, 'HH:mm:ss DD/MM/YYYY') : '';
    },
    handlePageChange(pageNum) {
      this.queryParams.page = pageNum ? parseInt(pageNum) : 1;
      this.fetchHistory();
    },
    onFilter() {
      this.queryParams.page = 1;
      this.fetchHistory();
    },
    async exportExcel() {
      if (this.isDownloading || !this.productBatchStockId) return;
      this.isDownloading = true;
      try {
        const response = await ApiService.query(
          `product-batch-stock/${this.productBatchStockId}/history/export-excel`,
          {
            params: {
              fromDate: formatDBDate(this.queryParams.fromDate),
              toDate: formatDBDate(this.queryParams.toDate),
              batchCode: this.batchCode,
            },
            responseType: 'blob',
          },
        );
        const fileName = fileDownload.getFileName(response);
        fileDownload.createFile(response.data, fileName);
        makeToastSuccess('Xuất Excel thành công');
      } catch (error) {
        makeToastFaile('Xuất Excel thất bại');
      } finally {
        this.isDownloading = false;
      }
    },
    stockSlipRoute(item) {
      const type = item.stockSlipType;
      if (type === 2) {
        return { name: 'update-export-stock', query: { id: item.stockSlipId } };
      }
      if (type === 3 || type === 5) {
        return {
          name: 'update-transfer-stock',
          query: { id: item.stockSlipId },
        };
      }
      return { name: 'update-stock', query: { id: item.stockSlipId } };
    },
    stockSlipHref(item) {
      const { href } = this.$router.resolve(this.stockSlipRoute(item));
      return href;
    },
  },
};
</script>

<style lang="scss" scoped>
.date-style {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 3;
  overflow: visible;
}
.history-table-scroll {
  max-height: calc(100vh - 16rem);
  overflow: auto;
}
::v-deep .modal-content,
::v-deep .modal-body {
  overflow: visible !important;
}
.stock-slip-link {
  cursor: pointer;
  color: #3699ff;
}
.stock-slip-link:hover {
  text-decoration: underline;
  opacity: 0.75;
}
::v-deep thead th {
  text-align: center !important;
  vertical-align: middle;
}
</style>
